// Funciones de transformación para convertir optimizaciones del backend al formato del frontend

/**
 * @typedef {Object} Rule
 * @property {number} id
 * @property {string} key
 * @property {string} name
 * @property {string} description
 * @property {Record<string, unknown>} [ui_metadata]
 */

/**
 * @typedef {Object} Group
 * @property {Rule|null} rule
 * @property {string|null} primary_kpi
 * @property {Object[]} suggestions
 */

/**
 * GET /api/get-optimizations response: { active: { groups: Group[] }, expired: { groups: Group[] } }
 * GET /api/get-active-insights response: { groups: Group[] }
 */

/**
 * Flattens an array of groups into a single array of suggestions.
 * @param {Group[]} groups
 * @returns {Object[]}
 */
export const groupsToSuggestions = (groups) => {
  if (!Array.isArray(groups)) return [];
  return groups.flatMap((g) => g.suggestions ?? []);
};

const normalizeRuleKey = (ruleKey) => {
  if (typeof ruleKey !== 'string') return null;
  const normalized = ruleKey.trim().toLowerCase();
  return normalized || null;
};

export const EXECUTABLE_ACTION_TYPES = new Set([
  'INCREASE_BUDGET', 'DECREASE_BUDGET',
  'PAUSE_AD', 'ENABLE_AD',
  'PAUSE_KEYWORD', 'ENABLE_KEYWORD', 'ADD_KEYWORD', 'ADD_NEGATIVE_KEYWORD',
]);

export const isExecutableActionType = (actionType) => {
  if (!actionType) return false;
  return EXECUTABLE_ACTION_TYPES.has(String(actionType).trim().toUpperCase());
};

export const SPLIT_TOP_SELLERS_WHEN_ROAS_VERY_HIGH_RULE_KEY = 'split_top_sellers_when_roas_very_high';
export const SPLIT_TOP_SELLERS_ACTION_TYPE_KEY = 'split_top_sellers';

export const isSplitTopSellersWhenRoasVeryHighRule = (ruleKey) =>
  [SPLIT_TOP_SELLERS_WHEN_ROAS_VERY_HIGH_RULE_KEY, SPLIT_TOP_SELLERS_ACTION_TYPE_KEY]
    .includes(normalizeRuleKey(ruleKey));

export const getSuggestionRuleKey = (suggestion) => {
  if (!suggestion || typeof suggestion !== 'object') return null;
  return normalizeRuleKey(suggestion.rule?.key ?? suggestion.action_type ?? null);
};

export const getGroupRuleKey = (group) => {
  if (!group || typeof group !== 'object') return null;
  const fromGroupRule = normalizeRuleKey(group.rule?.key ?? null);
  if (fromGroupRule) return fromGroupRule;
  const suggestions = Array.isArray(group.suggestions) ? group.suggestions : [];
  return getSuggestionRuleKey(suggestions[0]);
};

/**
 * Merges special-case groups used by shared UI flows.
 * Currently merges all groups that belong to split_top_sellers_when_roas_very_high
 * into a single group, preserving first-seen placement/order.
 * @param {Group[]} groups
 * @returns {Group[]}
 */
export const mergeSpecialOptimizationGroups = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) return [];

  const merged = [];
  let mergedSplitTopSellersGroup = null;

  groups.forEach((group) => {
    if (!group || typeof group !== 'object') return;
    const suggestions = Array.isArray(group.suggestions) ? group.suggestions : [];
    const ruleKey = getGroupRuleKey(group);

    if (isSplitTopSellersWhenRoasVeryHighRule(ruleKey)) {
      if (!mergedSplitTopSellersGroup) {
        mergedSplitTopSellersGroup = {
          ...group,
          rule: {
            ...(group.rule || {}),
            key: ruleKey || SPLIT_TOP_SELLERS_WHEN_ROAS_VERY_HIGH_RULE_KEY,
          },
          suggestions: [...suggestions],
        };
        merged.push(mergedSplitTopSellersGroup);
        return;
      }

      const existingIds = new Set(
        (mergedSplitTopSellersGroup.suggestions || [])
          .map((s) => s?.id)
          .filter((id) => id != null)
      );
      suggestions.forEach((suggestion) => {
        const suggestionId = suggestion?.id;
        if (suggestionId != null && existingIds.has(suggestionId)) return;
        mergedSplitTopSellersGroup.suggestions.push(suggestion);
        if (suggestionId != null) existingIds.add(suggestionId);
      });
      return;
    }

    merged.push(group);
  });

  return merged;
};

const dedupeCardsById = (cards) => {
  if (!Array.isArray(cards) || cards.length === 0) return [];
  const seenIds = new Set();
  const deduped = [];
  cards.forEach((card) => {
    const cardId = card?.id;
    if (cardId != null) {
      if (seenIds.has(cardId)) return;
      seenIds.add(cardId);
    }
    deduped.push(card);
  });
  return deduped;
};

/**
 * Builds detail modal groups as { ruleKey, cards } and guarantees split_top_sellers
 * suggestions are merged into a single group.
 * @param {Group[]} groups
 * @param {(objetivoId: number|string|null|undefined) => string|null} [getObjectiveName]
 * @returns {Array<{ ruleKey: string | null, cards: Object[] }>}
 */
export const buildDetailModalGroups = (groups, getObjectiveName) => {
  const mergedGroups = mergeSpecialOptimizationGroups(Array.isArray(groups) ? groups : []);
  const mappedGroups = mergedGroups.map((group) => {
    const suggestions = Array.isArray(group?.suggestions) ? group.suggestions : [];
    const firstSuggestion = suggestions[0];
    const cards = suggestions.map((suggestion) => suggestionToCard(suggestion, getObjectiveName));
    const inferredRuleKey =
      group?.rule?.key ??
      firstSuggestion?.rule?.key ??
      firstSuggestion?.action_type ??
      cards[0]?.rule_key ??
      null;
    return {
      ruleKey: inferredRuleKey ?? null,
      cards,
    };
  });

  if (mappedGroups.length <= 1) return mappedGroups;

  const nonSplitGroups = [];
  const splitCards = [];
  let splitInsertIndex = -1;

  mappedGroups.forEach((group) => {
    const cards = Array.isArray(group.cards) ? group.cards : [];
    const isSplitGroup =
      isSplitTopSellersWhenRoasVeryHighRule(group.ruleKey) ||
      cards.some((card) => isSplitTopSellersWhenRoasVeryHighRule(card?.rule_key));

    if (!isSplitGroup) {
      nonSplitGroups.push(group);
      return;
    }

    if (splitInsertIndex < 0) {
      splitInsertIndex = nonSplitGroups.length;
    }
    splitCards.push(...cards);
  });

  if (splitCards.length === 0) return mappedGroups;

  const mergedSplitGroup = {
    ruleKey: SPLIT_TOP_SELLERS_WHEN_ROAS_VERY_HIGH_RULE_KEY,
    cards: dedupeCardsById(splitCards),
  };

  const insertAt = Math.max(0, splitInsertIndex);
  nonSplitGroups.splice(insertAt, 0, mergedSplitGroup);
  return nonSplitGroups;
};

/**
 * Normalizes GET /api/get-optimizations response to { activeGroups, expiredGroups }.
 * Input: { active?: { groups?: Group[] }, expired?: { groups?: Group[] } }.
 * @param {Object} responseData
 * @returns {{ activeGroups: Group[], expiredGroups: Group[] }}
 */
export const normalizeSuggestionsResponse = (responseData) => {
  if (!responseData || typeof responseData !== 'object') {
    return { activeGroups: [], expiredGroups: [] };
  }
  const activeGroups = Array.isArray(responseData.active?.groups) ? responseData.active.groups : [];
  const expiredGroups = Array.isArray(responseData.expired?.groups) ? responseData.expired.groups : [];
  return { activeGroups, expiredGroups };
};

/**
 * Normalizes GET /api/get-active-insights response to { groups }.
 * Input: { groups?: Group[] }. Expects the new API contract only.
 * @param {Object} responseData
 * @returns {{ groups: Group[] }}
 */
export const normalizeGroupsResponse = (responseData) => {
  if (responseData && typeof responseData === 'object' && Array.isArray(responseData.groups)) {
    return { groups: responseData.groups };
  }
  return { groups: [] };
};

/**
 * Returns a stable key for a group for use as a React key. Avoids array index when possible.
 * Uses rule id/key when present; otherwise a composite of suggestion ids; fallbackIndex is only
 * used when the group has no rule and no suggestion ids.
 * @param {Group} group
 * @param {number} fallbackIndex
 * @returns {string}
 */
export const getGroupKey = (group, fallbackIndex) => {
  if (group?.rule != null) {
    if (group.rule.id != null) return String(group.rule.id);
    if (group.rule.key) return group.rule.key;
  }
  const suggestions = group?.suggestions ?? [];
  const ids = suggestions.map((s) => s.id).filter((id) => id != null);
  if (ids.length > 0) return ids.slice().sort().join('-');
  return `group-${fallbackIndex}`;
};

/**
 * Mapea action_type del backend a texto legible en español
 * Maneja tanto formato lowercase (increase_budget) como uppercase (INCREASE_BUDGET)
 */
export const mapActionTypeToAction = (actionType) => {
  if (!actionType) return 'Acción de optimización';
  
  const normalizedType = actionType.toLowerCase();
  
  const actionMap = {
    'increase_budget': 'Incrementar presupuesto',
    'decrease_budget': 'Reducir presupuesto',
    'pause_keyword': 'Pausar palabras clave',
    'enable_keyword': 'Habilitar palabras clave',
    'add_keyword': 'Agregar palabras clave',
    'add_negative_keyword': 'Agregar palabras clave negativas',
    'pause_ad': 'Pausar anuncio',
    'enable_ad': 'Habilitar anuncio',
    'adjust_target_roas': 'Ajustar Target ROAS',
    'adjust_target_cpa': 'Ajustar Target CPA',
    'exclude_placement': 'Excluir ubicación',
    'exclude_product': 'Excluir producto',
    'replace_asset': 'Reemplazar recurso',
    'refresh_creative': 'Actualizar creativo',
    'pause_entity': 'Pausar entidad',
    'enable_entity': 'Habilitar entidad',
    'budget_limited_alert': 'Alerta de presupuesto limitado',
    'pacing_alert': 'Alerta de ritmo',
    'adjust_cost_cap': 'Ajustar Cost Cap',
    'adjust_bid_cap': 'Ajustar Bid Cap',
    'adjust_min_roas': 'Ajustar Min ROAS',
    'change_keyword_match_type': 'Cambiar tipo de coincidencia',
    'pause_asset_group': 'Pausar grupo de recursos',
    'enable_asset_group': 'Habilitar grupo de recursos',
    'include_placement': 'Incluir ubicación',
    'include_product': 'Incluir producto',
    'move_product_to_campaign': 'Mover producto a campaña',
    'split_top_sellers': 'Dividir mejores vendedores',
    'create_campaign': 'Crear campaña',
    'duplicate_adset': 'Duplicar conjunto de anuncios',
    'adjust_adset_bid_amount': 'Ajustar puja del conjunto de anuncios',
    'iterate_winner_ad': 'Iterar anuncio ganador',
    'mitigate_audience_fatigue': 'Mitigar fatiga de audiencia',
    'create_reels_creative': 'Crear creativo de reels',
    'switch_to_advantage_plus': 'Cambiar a Advantage+',
    'enable_advantage_plus_placements': 'Habilitar ubicaciones Advantage+',
    'consolidate_adsets': 'Consolidar conjuntos de anuncios',
    'expand_audience_broad': 'Expandir audiencia broad',
    'optimize_landing_page_speed': 'Optimizar velocidad de landing page',
    'improve_landing_page_relevance': 'Mejorar relevancia de landing page',
    'no_action': 'Sin acción',
  };
  return actionMap[normalizedType] || actionType || 'Acción de optimización';
};

export const mapIntentToType = (intent) => {
  if (!intent) return 'OPTIMIZAR';
  
  const normalizedIntent = intent.toUpperCase();
  
  const intentMap = {
    'SCALE': 'ESCALAR',
    'EFFICIENCY': 'REDUCIR',
    'COST_CONTROL': 'REDUCIR',
    'CREATIVE': 'OPTIMIZAR',
    'HYGIENE': 'OPTIMIZAR',
  };
  return intentMap[normalizedIntent] || 'OPTIMIZAR';
};

export const transformItemsToDetails = (items) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items.map(item => {
    const metrics = item.metrics || {};
    const detail = {
      keyword: item.item_text || item.item_id || '',
      name: item.item_text || item.item_id || '',
      term: item.item_text || item.item_id || '',
      spend: metrics.spend || metrics.cost || 0,
      roas: metrics.roas || 0,
      cpa: metrics.cpa || 0,
      conversions: metrics.conversions || 0,
    };
    return detail;
  });
};

const ENTITY_LABELS = {
  KEYWORD: 'Palabra Clave',
  AD: 'Anuncio',
  AD_GROUP: 'Conjunto de anuncios',
  ADSET: 'Conjunto de anuncios',
  CAMPAIGN: 'Campaña',
  PLACEMENT: 'Ubicación',
  OFFER: 'Producto',
  ASSET: 'Recurso',
  SEARCH_TERM: 'Término de Búsqueda',
  NEGATIVE_KEYWORD: 'Palabra Clave Negativa',
};

const getEntityLabel = (entityType) => ENTITY_LABELS[entityType] || entityType || 'Detalle';

/**
 * Returns canonical row metrics from an object. Only keys spend, roas, conversions, cpa are read.
 * Null/undefined/NaN become null. No fallbacks or aliases (e.g. no cost -> spend).
 * @param {{ spend?: number, roas?: number, conversions?: number, cpa?: number } | null | undefined} metrics
 * @returns {{ spend: number | null, roas: number | null, conversions: number | null, cpa: number | null }}
 */
export const canonicalRowMetrics = (metrics) => {
  const m = metrics && typeof metrics === 'object' ? metrics : {};
  const toNull = (v) => (v != null && !Number.isNaN(v) && Number.isFinite(v) ? Number(v) : null);
  return {
    spend: toNull(m.spend),
    roas: toNull(m.roas),
    conversions: toNull(m.conversions),
    cpa: toNull(m.cpa),
  };
};

const buildActionChange = (suggestion) => {
  const actionType = (suggestion.action_type || '').toLowerCase();
  const before = suggestion.value_before ?? suggestion.action_params?.value_before ?? null;
  const after = suggestion.value_after ?? suggestion.action_params?.value_after ?? null;

  if (before === null && after === null) {
    let label = 'Valor';
    let format = 'number';
    if (actionType.includes('budget')) {
      label = 'Presupuesto diario';
      format = 'currency';
    } else if (actionType.includes('roas')) {
      label = actionType.includes('target') ? 'ROAS objetivo' : 'ROAS';
      format = 'roas_percent';
    } else if (actionType.includes('cpa')) {
      label = actionType.includes('target') ? 'CPA objetivo' : 'CPA';
      format = 'currency';
    } else if (actionType.includes('cost_cap')) {
      label = 'Cost Cap';
      format = 'currency';
    } else if (actionType.includes('bid')) {
      label = 'Puja';
      format = 'currency';
    }
    return { label, format, before: null, after: null };
  }

  let label = 'Valor';
  let format = 'number';
  if (actionType.includes('budget')) {
    label = 'Presupuesto diario';
    format = 'currency';
  } else if (actionType.includes('roas')) {
    label = actionType.includes('target') ? 'ROAS objetivo' : 'ROAS';
    format = 'roas_percent';
  } else if (actionType.includes('cpa')) {
    label = actionType.includes('target') ? 'CPA objetivo' : 'CPA';
    format = 'currency';
  } else if (actionType.includes('cost_cap')) {
    label = 'Cost Cap';
    format = 'currency';
  } else if (actionType.includes('bid')) {
    label = 'Puja';
    format = 'currency';
  }
  return { label, format, before, after };
};

const buildItemsFromApiSuggestion = (suggestion) => {
  const actionChange = buildActionChange(suggestion);
  const rawItems = Array.isArray(suggestion.items) ? suggestion.items : [];
  const normalizedActionType = String(suggestion.action_type || '').toLowerCase();
  const isKeywordOrSearchTermSuggestion = [
    'pause_keyword',
    'enable_keyword',
    'add_keyword',
    'add_negative_keyword',
    'change_keyword_match_type',
    'pause_search_term',
    'exclude_search_term',
  ].includes(normalizedActionType);

  if (rawItems.length > 0) {
    return rawItems.map((item) => {
      const action = item.action && typeof item.action === 'object' ? item.action : {};
      const pt = String(action.parent_type || '').toUpperCase();
      const adGroupFromAction = ['AD_GROUP', 'ADSET', 'AD_GROUP_ID'].includes(pt)
        ? (action.parent_id != null && String(action.parent_id).trim() !== ''
          ? String(action.parent_id).trim()
          : null)
        : null;
      const criterionExternal =
        item.target_entity && item.target_entity.external_id != null
          ? String(item.target_entity.external_id).trim()
          : null;
      return {
        id: criterionExternal ?? item.item_id ?? item.id ?? item.item_text,
        name: item.item_text ?? item.item_id ?? item.item_name ?? '',
        type: item.item_type ?? suggestion.primary_entity?.type ?? 'ITEM',
        type_label: getEntityLabel(item.item_type ?? suggestion.primary_entity?.type),
        metrics: canonicalRowMetrics(item.metrics),
        metrics_extended: item.metrics_extended && typeof item.metrics_extended === 'object'
          ? item.metrics_extended
          : null,
        ui_metrics: item.metrics_extended && typeof item.metrics_extended === 'object'
          ? item.metrics_extended
          : null,
        action_change: actionChange,
        budget_before: null,
        budget_after: null,
        criteria_evaluation: item.criteria_evaluation ?? null,
        value_change_display: item.value_change ?? null,
        criterion_external_id: criterionExternal,
        ad_group_id: adGroupFromAction,
      };
    });
  }

  if (isKeywordOrSearchTermSuggestion) {
    const evidence = suggestion.evidence && typeof suggestion.evidence === 'object' ? suggestion.evidence : null;
    if (evidence) {
      const tableLikeKeys = Object.keys(evidence).filter((key) => {
        const value = evidence[key];
        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] != null &&
          typeof value[0] === 'object' &&
          !Array.isArray(value[0])
        );
      });

      const rows = tableLikeKeys.flatMap((key) => evidence[key] || []);
      const keywordOrTermRows = rows.filter((row) => (
        row?.search_term != null ||
        row?.keyword != null ||
        row?.term != null ||
        row?.item_text != null
      ));

      if (keywordOrTermRows.length > 0) {
        return keywordOrTermRows.map((row, idx) => {
          const critRaw = row.criterion_id ?? row.id ?? row.keyword_id ?? row.item_id;
          const criterion_external_id =
            critRaw != null && critRaw !== '' ? String(critRaw).trim() : null;
          const agRaw = row.ad_group_id ?? row.adgroup_id;
          const ad_group_id =
            agRaw != null && agRaw !== '' ? String(agRaw).trim() : null;
          return {
            id: criterion_external_id ?? row?.item_id ?? `${suggestion.id}-${idx}`,
            name: row?.search_term ?? row?.keyword ?? row?.term ?? row?.item_text ?? '-',
            type: row?.keyword != null ? 'KEYWORD' : 'SEARCH_TERM',
            type_label: row?.keyword != null ? 'Palabra Clave' : 'Término de Búsqueda',
            metrics: canonicalRowMetrics(row?.metrics ?? row),
            ui_metrics: null,
            action_change: actionChange,
            budget_before: null,
            budget_after: null,
            criterion_external_id,
            ad_group_id,
          };
        });
      }
    }
  }

  const entity = suggestion.primary_entity || suggestion.scope_entity;
  const hasBudget = suggestion.value_before != null || suggestion.value_after != null;
  if (entity || hasBudget) {
    const name = entity?.name ?? suggestion.campaign?.name ?? 'Elemento';
    const type = entity?.type ?? 'ITEM';
    const evidenceMetrics = suggestion.evidence?.metrics ?? {};
    return [
      {
        id: entity?.id ?? entity?.external_id ?? suggestion.id,
        name,
        type,
        type_label: getEntityLabel(type),
        metrics: canonicalRowMetrics(evidenceMetrics),
        ui_metrics: suggestion.evidence?.ui_metrics ?? null,
        action_change: actionChange,
        budget_before: suggestion.value_before ?? null,
        budget_after: suggestion.value_after ?? null,
      },
    ];
  }

  return [];
};

/**
 * One API suggestion -> one UI card (building block for Kanban group cards).
 */
export const suggestionToCard = (suggestion, getObjectiveName) => {
  const campaign = suggestion.campaign || {};
  const campaignName = campaign.name || suggestion.campaign_name || 'Campaña';
  const objectiveName = getObjectiveName ? getObjectiveName(suggestion.objetivo_id) : null;
  const description = suggestion.rule?.description || suggestion.user_text || suggestion.rule?.name || 'Optimización sugerida';
  const items = buildItemsFromApiSuggestion(suggestion);
  const v2Items = Array.isArray(suggestion.items) ? suggestion.items : [];
  const ui = suggestion.ui_metrics || suggestion.evidence?.ui_metrics || {};
  const metrics =
    items.length === 1
      ? items[0].metrics
      : { spend: null, roas: null, cpa: null, conversions: null };
  const appliedAt = suggestion.applied_at ?? suggestion.appliedAt ?? null;

  return {
    id: suggestion.id,
    created_at: suggestion.created_at ?? suggestion.generated_at ?? null,
    type: mapIntentToType(suggestion.intent),
    action: suggestion.action_label ?? mapActionTypeToAction(suggestion.action_type),
    action_type: suggestion.action_type ?? null,
    platform: mapPlatformToDisplay(suggestion.platform),
    objective_name: objectiveName || 'Objetivo',
    campaign_name: campaignName,
    intent: suggestion.intent ?? null,
    primary_entity_name: suggestion.primary_entity?.name ?? null,
    primary_entity_external_id: suggestion.primary_entity?.external_id ?? suggestion.primary_entity?.id ?? null,
    primary_entity: suggestion.primary_entity ?? null,
    primary_entity_breadcrumb: Array.isArray(suggestion.primary_entity_breadcrumb) ? suggestion.primary_entity_breadcrumb : [],
    description,
    impact_metric: suggestion.primary_kpi || 'ROAS',
    details_label: getEntityLabel(suggestion.primary_entity?.type || suggestion.scope_entity?.type),
    expires_at: suggestion.expires_at ?? null,
    is_liked: suggestion.is_liked ?? null,
    target_roas: ui.target_roas ?? null,
    target_cpa: ui.target_cpa ?? null,
    currency: suggestion.value_unit ?? suggestion.action_params?.value_unit ?? suggestion.metrics?.currency ?? suggestion.currency ?? null,
    rule_key: suggestion.rule?.key ?? suggestion.action_type ?? null,
    rule_ui_metadata: suggestion.rule?.ui_metadata ?? null,
    metrics: suggestion.metrics ?? null,
    source_ids: [suggestion.id],
    applied_at: appliedAt,
    items: v2Items,
    campaign_groups: [
      {
        campaign_id: campaign.external_id ?? null,
        campaign_name: campaignName,
        objective_name: objectiveName,
        metrics,
        items,
      },
    ],
    campaigns_count: 1,
    user_text: suggestion.user_text ?? null,
    evidence: suggestion.evidence ?? null,
    status: suggestion.status ?? null,
    archetype: suggestion.archetype ?? null,
    criteria: suggestion.criteria ?? null,
    metrics_extended: suggestion.metrics_extended ?? null,
    window_comparison: suggestion.window_comparison ?? null,
    value_change_display: suggestion.value_change_display ?? null,
    action_label: suggestion.action_label ?? null,
    value_before: suggestion.value_before ?? null,
    value_after: suggestion.value_after ?? null,
    value_unit: suggestion.value_unit ?? suggestion.action_params?.value_unit ?? null,
    _backend: {
      value_before: suggestion.value_before,
      value_after: suggestion.value_after,
      value_unit: suggestion.value_unit ?? suggestion.action_params?.value_unit ?? null,
    },
    // Full raw v2 suggestion, kept so the data-driven SuggestionCard (admin modal)
    // has the fields suggestionToCard intentionally drops (diagnostic_score, sub_items,
    // structural_change, account_benchmarks, daily_series, peer_comparison, …).
    _raw: suggestion,
  };
};

/**
 * Kanban column definitions for the optimization board.
 */
export const KANBAN_COLUMNS = [
  { key: 'pendientes', label: 'Pendientes', statuses: ['NEW', 'SHOWN', 'LIKED'], color: 'blue' },
  { key: 'aplicadas', label: 'Aplicadas', statuses: ['APPLIED'], color: 'green' },
  { key: 'descartadas', label: 'Descartadas', statuses: ['DISMISSED'], color: 'amber' },
  { key: 'expiradas', label: 'Expiradas', statuses: ['EXPIRED'], color: 'gray' },
];

/** Opciones para el filtro de estatus (alineadas con las columnas del Kanban). */
export const KANBAN_ESTADO_FILTER_OPTIONS = [
  { value: 'todas', label: 'Todos' },
  ...KANBAN_COLUMNS.map((c) => ({ value: c.key, label: c.label })),
];

const KANBAN_COLUMN_KEY_SET = new Set(KANBAN_COLUMNS.map((c) => c.key));

/**
 * @param {string | null | undefined} raw
 * @returns {'todas' | 'pendientes' | 'aplicadas' | 'descartadas' | 'expiradas'}
 */
export const normalizeKanbanEstadoFilterParam = (raw) => {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (!v || v === 'todas' || v === 'todos') return 'todas';
  if (KANBAN_COLUMN_KEY_SET.has(v)) return /** @type {'pendientes' | 'aplicadas' | 'descartadas' | 'expiradas'} */ (v);
  return 'todas';
};

/**
 * Groups an array of cards into Kanban column buckets by status.
 * @param {Object[]} cards - Cards produced by suggestionToCard()
 * @returns {Object} - { pendientes: Card[], aplicadas: Card[], descartadas: Card[], expiradas: Card[] }
 */
export const groupCardsByKanbanColumn = (cards) => {
  const buckets = {};
  for (const col of KANBAN_COLUMNS) {
    buckets[col.key] = [];
  }
  const statusToColumn = {};
  for (const col of KANBAN_COLUMNS) {
    for (const s of col.statuses) {
      statusToColumn[s] = col.key;
    }
  }
  for (const card of cards) {
    const status = card.status ? String(card.status).toUpperCase() : null;
    let columnKey = status ? statusToColumn[status] : null;
    if (!columnKey) {
      // Fallback: infer from expires_at
      if (card.expires_at && new Date(card.expires_at) < new Date()) {
        columnKey = 'expiradas';
      } else {
        columnKey = 'pendientes';
      }
    }
    buckets[columnKey].push(card);
  }
  return buckets;
};

/** Lower number = shown first when a group has mixed child statuses. */
const KANBAN_COLUMN_PRIORITY = {
  pendientes: 0,
  aplicadas: 1,
  descartadas: 2,
  expiradas: 3,
};

/**
 * Resolves which Kanban column a single suggestion card belongs to (same rules as groupCardsByKanbanColumn).
 * @param {Object} card - Output of suggestionToCard()
 * @returns {string} KANBAN_COLUMNS key
 */
export const resolveCardKanbanColumnKey = (card) => {
  if (!card || typeof card !== 'object') return 'pendientes';
  const statusToColumn = {};
  for (const col of KANBAN_COLUMNS) {
    for (const s of col.statuses) {
      statusToColumn[s] = col.key;
    }
  }
  const status = card.status ? String(card.status).toUpperCase() : null;
  let columnKey = status ? statusToColumn[status] : null;
  if (!columnKey) {
    if (card.expires_at && new Date(card.expires_at) < new Date()) {
      columnKey = 'expiradas';
    } else {
      columnKey = 'pendientes';
    }
  }
  return columnKey;
};

/**
 * When a group has children in different columns, pick the column that needs attention first
 * (pendientes before aplicadas, etc.).
 * @param {Object[]} childCards - suggestionToCard() results
 * @returns {string}
 */
export const resolveGroupKanbanColumnKey = (childCards) => {
  if (!Array.isArray(childCards) || childCards.length === 0) return 'pendientes';
  let bestKey = 'expiradas';
  let bestP = Infinity;
  for (const c of childCards) {
    const col = resolveCardKanbanColumnKey(c);
    const p = KANBAN_COLUMN_PRIORITY[col] ?? 99;
    if (p < bestP) {
      bestP = p;
      bestKey = col;
    }
  }
  return bestKey;
};

/**
 * Builds one Kanban row from an API group (rule + suggestions), after mergeSpecialOptimizationGroups.
 * @param {Group} apiGroup
 * @param {(id: *) => string|null|undefined} getObjectiveName
 * @param {number} fallbackIndex
 * @returns {Object|null}
 */
export const buildKanbanGroupCardFromApiGroup = (apiGroup, getObjectiveName, fallbackIndex = 0) => {
  const suggestions = Array.isArray(apiGroup?.suggestions) ? apiGroup.suggestions : [];
  if (suggestions.length === 0) return null;

  const childCards = suggestions.map((s) => suggestionToCard(s, getObjectiveName));
  const first = childCards[0];
  const n = childCards.length;
  const rule = apiGroup?.rule || {};

  const stableId = `kanban-grp-${getGroupKey(apiGroup, fallbackIndex)}`;
  const source_ids = suggestions.map((s) => s.id).filter((id) => id != null);

  const actionTitle =
    n > 1
      ? (rule.name || rule.description || first.description || first.action)
      : first.action;

  const campaignNames = [...new Set(childCards.map((c) => c.campaign_name).filter(Boolean))];
  const campaign_name =
    campaignNames.length === 0
      ? first.campaign_name
      : campaignNames.length === 1
      ? campaignNames[0]
      : `${campaignNames.length} campañas`;

  const likes = childCards.map((c) => c.is_liked);
  const aggregatedLike =
    likes.every((x) => x === true) ? true : likes.every((x) => x === false) ? false : null;

  let minExpires = null;
  let expiresPick = first.expires_at;
  for (const c of childCards) {
    if (!c.expires_at) continue;
    const t = new Date(c.expires_at).getTime();
    if (Number.isNaN(t)) continue;
    if (minExpires === null || t < minExpires) {
      minExpires = t;
      expiresPick = c.expires_at;
    }
  }

  let appliedPick = first.applied_at ?? null;
  let maxApplied = appliedPick ? new Date(appliedPick).getTime() : null;
  for (const c of childCards) {
    if (!c.applied_at) continue;
    const t = new Date(c.applied_at).getTime();
    if (Number.isNaN(t)) continue;
    if (maxApplied === null || t > maxApplied) {
      maxApplied = t;
      appliedPick = c.applied_at;
    }
  }

  return {
    id: stableId,
    isGroupCard: n > 1,
    suggestionCount: n,
    childCards,
    source_ids,
    type: first.type,
    action: actionTitle,
    action_type: first.action_type,
    platform: first.platform,
    objective_name: first.objective_name,
    campaign_name,
    primary_entity_name: n === 1 ? first.primary_entity_name : null,
    description: rule.description || first.description,
    impact_metric: first.impact_metric,
    expires_at: expiresPick,
    is_liked: aggregatedLike,
    target_roas: first.target_roas,
    target_cpa: first.target_cpa,
    currency: first.currency,
    rule_key: first.rule_key,
    rule_ui_metadata: first.rule_ui_metadata,
    metrics: first.metrics,
    campaign_groups: first.campaign_groups,
    campaigns_count: first.campaigns_count,
    user_text: first.user_text,
    evidence: first.evidence,
    status: null,
    created_at: first.created_at,
    applied_at: appliedPick,
    _backend: first._backend,
  };
};

/**
 * Stable key to merge API groups that refer to the same optimization (same rule scope + campaign
 * + action + primary entity). The backend may return separate group rows per suggestion status.
 * @param {Group} group
 * @returns {string}
 */
export const getApiGroupDedupeKey = (group) => {
  const suggestions = Array.isArray(group?.suggestions) ? group.suggestions : [];
  const s = suggestions[0];
  if (!s) {
    return getGroupKey(group, 0);
  }
  const rulePart =
    group?.rule?.id ??
    s?.rule?.id ??
    group?.rule?.key ??
    s?.rule?.key ??
    '';
  const camp =
    s?.campaign?.external_id ??
    s?.campaign?.id ??
    s?.campaign_name ??
    (typeof s?.campaign === 'string' ? s.campaign : '') ??
    '';
  const action = (s?.action_type ?? '').toString().toUpperCase();
  const entity =
    (s?.primary_entity && (s.primary_entity.external_id ?? s.primary_entity.id)) ??
    (s?.scope_entity && (s.scope_entity.external_id ?? s.scope_entity.id)) ??
    '';
  const objetivo = s?.objetivo_id != null ? String(s.objetivo_id) : '';
  return ['v1', String(rulePart), String(camp), action, String(entity), objetivo].join('\u0001');
};

/**
 * Merges API groups with the same logical dedupe key (concatenates suggestions, dedupes by id).
 * Preserves first-seen group order.
 * @param {Group[]} groups
 * @returns {Group[]}
 */
export const mergeApiGroupsByLogicalDedupeKey = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) return [];
  const map = new Map();
  for (const g of groups) {
    if (!g) continue;
    const key = getApiGroupDedupeKey(g);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        rule: g.rule ?? null,
        primary_kpi: g.primary_kpi,
        suggestions: [...(g.suggestions || [])],
      });
      continue;
    }
    const seen = new Set((existing.suggestions || []).map((x) => x?.id).filter((id) => id != null));
    for (const sug of g.suggestions || []) {
      if (sug?.id != null && seen.has(sug.id)) continue;
      if (sug?.id != null) seen.add(sug.id);
      existing.suggestions.push(sug);
    }
    if (!existing.rule && g.rule) existing.rule = g.rule;
  }
  return [...map.values()];
};

/**
 * Active + expired API groups → merged (split_top_sellers, etc.) → one Kanban card per group.
 */
export const buildKanbanGroupCardsFromFilteredGroups = (
  filteredActiveGroups,
  filteredExpiredGroups,
  getObjectiveName
) => {
  const mergedActive = mergeApiGroupsByLogicalDedupeKey(
    mergeSpecialOptimizationGroups(Array.isArray(filteredActiveGroups) ? filteredActiveGroups : [])
  );
  const mergedExpired = mergeApiGroupsByLogicalDedupeKey(
    mergeSpecialOptimizationGroups(Array.isArray(filteredExpiredGroups) ? filteredExpiredGroups : [])
  );
  const out = [];
  let idx = 0;
  for (const g of mergedActive) {
    const card = buildKanbanGroupCardFromApiGroup(g, getObjectiveName, idx);
    if (card) out.push(card);
    idx += 1;
  }
  for (const g of mergedExpired) {
    const card = buildKanbanGroupCardFromApiGroup(g, getObjectiveName, idx);
    if (card) out.push(card);
    idx += 1;
  }
  return out;
};

/**
 * Places group Kanban cards into column buckets (one card per API group).
 * @param {Object[]} groupCards - From buildKanbanGroupCardsFromFilteredGroups
 */
export const distributeKanbanGroupCardsToBuckets = (groupCards) => {
  const buckets = {};
  for (const col of KANBAN_COLUMNS) {
    buckets[col.key] = [];
  }
  if (!Array.isArray(groupCards)) return buckets;
  for (const gc of groupCards) {
    if (!gc) continue;
    const colKey =
      gc.childCards && gc.childCards.length > 0
        ? resolveGroupKanbanColumnKey(gc.childCards)
        : resolveCardKanbanColumnKey(gc);
    buckets[colKey].push(gc);
  }
  return buckets;
};

/**
 * Expands group cards to a flat list of suggestion cards for modal navigation within a column.
 * @param {Object[]} cards - Kanban group cards (or mixed)
 */
export const flattenKanbanCardsForModalNavigation = (cards) => {
  if (!Array.isArray(cards)) return [];
  return cards.flatMap((c) => {
    if (c?.childCards && c.childCards.length > 0) {
      return c.childCards;
    }
    return c ? [c] : [];
  });
};

/** Evidence key to Spanish title for the evidence modal */
const EVIDENCE_KEY_TITLES = {
  related_search_terms: 'Términos de búsqueda relacionados',
};

const humanizeEvidenceKey = (key) => {
  if (!key || typeof key !== 'string') return 'Evidencia';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Keys that may hold the "entity name" in a row; first present is used. */
const NAME_KEYS = ['search_term', 'item_text', 'name', 'keyword', 'term'];

/**
 * Returns keys in evidence whose value is a non-empty array of plain objects (table-like rows).
 * Skips ui_metrics and non-array values.
 * @param {Object|null} evidence - suggestion.evidence
 * @returns {string[]}
 */
export const getTableEvidenceKeys = (evidence) => {
  if (!evidence || typeof evidence !== 'object') return [];
  return Object.keys(evidence).filter((key) => {
    if (key === 'ui_metrics') return false;
    const value = evidence[key];
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      value[0] != null &&
      typeof value[0] === 'object' &&
      !Array.isArray(value[0])
    );
  });
};

/**
 * Normalizes suggestion.evidence into an array of { title, entries } for the evidence modal.
 * One table per table-like key; entries are { name, metrics } (name from row entity key, metrics = rest).
 * @param {Object|null} evidence - suggestion.evidence
 * @param {string|null} currencyCode - Currency code used to format monetary columns.
 * @returns {Array<{ title: string, entries: Array<{ name: string, metrics: Object }> }>}
 */
export const getEvidenceTablesForModal = (evidence, currencyCode = null) => {
  const keys = getTableEvidenceKeys(evidence);
  if (keys.length === 0) return [];
  const tables = [];
  for (const evidenceKey of keys) {
    const rawRows = evidence[evidenceKey];
    const title = EVIDENCE_KEY_TITLES[evidenceKey] || humanizeEvidenceKey(evidenceKey);
    const entries = rawRows.map((row) => {
      const nameKey = NAME_KEYS.find((k) => row[k] != null && row[k] !== '');
      const name = nameKey != null ? String(row[nameKey]) : String(row.id ?? '');
      const metrics = { ...row };
      // Remove only the single key used as the entity name. Do not remove other NAME_KEYS from metrics,
      // since they may be legitimate metric columns (e.g. keyword, term) and must be preserved.
      if (nameKey != null) delete metrics[nameKey];
      delete metrics.id;
      return { name, metrics };
    });
    tables.push({ title, entries, currencyCode });
  }
  return tables;
};

/** Known metric keys in preferred order for evidence table columns; canonical keys only. Spanish labels */
const EVIDENCE_METRIC_ORDER = ['spend', 'roas', 'cpa', 'conversions', 'clicks', 'impressions'];
const EVIDENCE_METRIC_LABELS = {
  spend: 'Gasto',
  roas: 'ROAS',
  cpa: 'CPA',
  conversions: 'Conv.',
  clicks: 'Clics',
  impressions: 'Impr.',
};

/**
 * Infer table columns from evidence entries: one entity column (name) plus one per metrics key.
 * @param {Array<{ name: string, metrics: Object }>} entries
 * @returns {Array<{ key: string, label: string, format: 'text'|'currency'|'percent'|'number'>}
 */
export const inferEvidenceColumns = (entries) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [{ key: 'name', label: 'Entidad', format: 'text' }];
  }
  const metricKeys = new Set();
  entries.forEach((row) => {
    if (row.metrics && typeof row.metrics === 'object') {
      Object.keys(row.metrics).forEach((k) => metricKeys.add(k));
    }
  });
  const ordered = [];
  EVIDENCE_METRIC_ORDER.forEach((k) => {
    if (metricKeys.has(k)) {
      ordered.push(k);
      metricKeys.delete(k);
    }
  });
  const rest = [...metricKeys].sort();
  const columns = [{ key: 'name', label: 'Entidad', format: 'text' }];
  [...ordered, ...rest].forEach((key) => {
    const label = EVIDENCE_METRIC_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const format = ['spend', 'cpa'].includes(key) ? 'currency' : key === 'roas' ? 'percent' : 'number';
    columns.push({ key, label, format });
  });
  return columns;
};

/**
 * Format a single evidence cell value for display.
 * @param {string} format - 'text' | 'currency' | 'percent' | 'number'
 * @param {*} value
 * @param {string|null} currencyCode
 * @returns {string}
 */
export const formatEvidenceCellValue = (format, value, currencyCode = null) => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: currencyCode || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currencyDisplay: 'code',
      }).format(Number(value));
    case 'percent':
      return `${(Number(value) * 100).toFixed(0)}%`;
    case 'number':
      return String(Number(value));
    default:
      return String(value);
  }
};

export const mapPlatformToDisplay = (platform) => {
  const platformMap = {
    'Google': 'Google Ads',
    'GOOGLE': 'Google Ads',
    'Meta': 'Meta Ads',
    'META': 'Meta Ads',
  };
  return platformMap[platform] || platform || 'Plataforma';
};

/**
 * Cards use `mapPlatformToDisplay` so `suggestion.platform` is often "Google Ads", not the API enum "GOOGLE".
 * Use this for UI logic (match type, etc.) that must work with both shapes.
 */
export const isGoogleAdsPlatformUi = (platform) => {
  if (platform == null || platform === '') return false;
  const p = String(platform).trim().toUpperCase();
  return p === 'GOOGLE' || p === 'GOOGLE ADS';
};
